import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Shield, Users, Eye, Lock, X, Mail } from 'lucide-react';

export default function FamilySafety() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 transition-colors"
            style={{ color: 'var(--fy-text-secondary)' }}
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--fy-background-secondary)' }}>
              <img src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" alt="LikeKind" className="w-8 h-8 object-cover" />
            </div>
            <span className="font-bold" style={{ color: 'var(--fy-text-primary)' }}>LikeKind</span>
          </div>
          <div className="w-20"></div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        {/* Title */}
        <div className="flex items-center gap-3 mb-2">
          <Shield className="w-8 h-8" style={{ color: 'var(--lk-sage)' }} />
          <h1 
            className="text-3xl sm:text-4xl font-bold" 
            style={{ color: 'var(--fy-text-primary)', fontFamily: 'Outfit' }}
          >
            Family Safety Overview
          </h1>
        </div>
        <p className="mb-10" style={{ color: 'var(--fy-text-disabled)' }}>
          A quick explanation for parents
        </p>

        {/* Content */}
        <div className="space-y-8">
          
          {/* Intro */}
          <section className="p-5 rounded-xl" style={{ backgroundColor: 'var(--lk-sage-light)', border: '1px solid var(--lk-sage)' }}>
            <p className="text-lg leading-relaxed" style={{ color: 'var(--fy-text-primary)' }}>
              LikeKind is designed to be a child's first account — structured, supervised, and calm.
            </p>
            <p className="mt-2 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              It was built with the assumption that children should not navigate digital spaces alone.
            </p>
          </section>

          {/* How Children Use LikeKind */}
          <section>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
              <Users className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
              How Children Use LikeKind
            </h2>
            <ul className="space-y-2" style={{ color: 'var(--fy-text-secondary)' }}>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage)' }}></span>
                Children cannot create accounts independently.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage)' }}></span>
                A parent or guardian must create and manage the account first.
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage)' }}></span>
                Children participate through the family structure.
              </li>
            </ul>
          </section>

          {/* What Strangers Cannot Do */}
          <section>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
              <Lock className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
              What Strangers Cannot Do
            </h2>
            <p className="mb-3 font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Children under 13:
            </p>
            <ul className="space-y-2 mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#DC2626' }} />
                Cannot be searched
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#DC2626' }} />
                Cannot be discovered
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#DC2626' }} />
                Cannot receive messages from strangers
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#DC2626' }} />
                Cannot message other children directly
              </li>
            </ul>
            <p className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              These restrictions are structural and cannot be turned off.
            </p>
          </section>

          {/* Parent Visibility */}
          <section>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
              <Eye className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
              Parent Visibility
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              Parents have visibility into child accounts.
            </p>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              This allows parents to:
            </p>
            <ul className="space-y-2 mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage)' }}></span>
                See activity
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage)' }}></span>
                Approve certain uploads
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage)' }}></span>
                Manage privacy settings
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--lk-sage)' }}></span>
                Control participation
              </li>
            </ul>
            <p className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Children are always aware when parent visibility is active.
            </p>
          </section>

          {/* Teen Accounts */}
          <section className="p-4 rounded-lg" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
            <h2 className="text-lg font-bold mb-3" style={{ color: 'var(--fy-text-primary)' }}>
              Teen Accounts (Ages 13–15)
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              Teen accounts allow gradual independence. However:
            </p>
            <ul className="space-y-1" style={{ color: 'var(--fy-text-secondary)' }}>
              <li>• Parent visibility remains active by default</li>
              <li>• Discoverability remains limited</li>
              <li>• Teens cannot override safety protections</li>
            </ul>
          </section>

          {/* Youth Accounts */}
          <section className="p-4 rounded-lg" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
            <h2 className="text-lg font-bold mb-3" style={{ color: 'var(--fy-text-primary)' }}>
              Youth Accounts (Ages 16–17)
            </h2>
            <p className="leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              Youth accounts allow broader participation while still operating inside safety rules. Parents may adjust visibility settings depending on family preference.
            </p>
          </section>

          {/* What LikeKind Does NOT Do */}
          <section>
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              What LikeKind Does NOT Do
            </h2>
            <p className="mb-3 leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind does not:
            </p>
            <ul className="space-y-2" style={{ color: 'var(--fy-text-secondary)' }}>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                Sell personal data
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                Use behavioral advertising
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                Rank children by popularity
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                Track attention or engagement patterns
              </li>
              <li className="flex items-start gap-2">
                <X className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                Encourage addictive use
              </li>
            </ul>
          </section>

          {/* Our Approach */}
          <section className="p-5 rounded-xl" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
            <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--fy-text-primary)' }}>
              Our Approach
            </h2>
            <p className="leading-relaxed mb-2" style={{ color: 'var(--fy-text-secondary)' }}>
              Most platforms try to fix safety with moderation.
            </p>
            <p className="leading-relaxed mb-2" style={{ color: 'var(--fy-text-secondary)' }}>
              LikeKind approaches safety through structure.
            </p>
            <p className="leading-relaxed font-medium" style={{ color: 'var(--fy-text-primary)' }}>
              Children are protected by how the platform is designed.
            </p>
          </section>

          {/* Questions */}
          <section className="pt-6" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--fy-text-primary)' }}>
              <Mail className="w-5 h-5" style={{ color: 'var(--lk-sage)' }} />
              Questions
            </h2>
            <p className="leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
              If you have questions about family accounts or child safety:
            </p>
            <a 
              href="mailto:privacy@likekind.com" 
              className="inline-block mt-2 px-4 py-2 rounded-full font-medium"
              style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}
            >
              privacy@likekind.com
            </a>
          </section>

          {/* Related Links */}
          <section className="pt-6" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Learn More
            </h3>
            <div className="flex flex-wrap gap-3">
              <Link 
                to="/parent-visibility" 
                className="px-4 py-2 rounded-full text-sm transition-colors"
                style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
              >
                Parent Visibility Details →
              </Link>
              <Link 
                to="/trust" 
                className="px-4 py-2 rounded-full text-sm transition-colors"
                style={{ backgroundColor: 'var(--fy-background-secondary)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}
              >
                Trust & Safety →
              </Link>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 py-8" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p style={{ color: 'var(--fy-text-disabled)' }}>
            © {new Date().getFullYear()} LikeKind Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
